import { useState } from 'react';
import { InterviewFeedback, RevisedFeedback, ReviewSession } from '@recruitai/shared';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

interface ReviewCompareProps {
  originalFeedback: InterviewFeedback;
  revisedFeedback: RevisedFeedback;
  reviewSessions?: ReviewSession[];
  reviewCount: number;
}

function ScoreDelta({ original, revised, label }: { original: number; revised: number; label: string }) {
  const diff = revised - original;
  const Icon = diff > 0 ? ArrowUp : diff < 0 ? ArrowDown : Minus;
  const color = diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-muted-foreground';

  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">{original}</span>
        <Icon className={`h-3 w-3 ${color}`} />
        <span className={`text-sm font-medium ${color}`}>{revised}</span>
      </div>
    </div>
  );
}

export function ReviewCompare({
  originalFeedback,
  revisedFeedback,
  reviewSessions,
  reviewCount,
}: ReviewCompareProps) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(reviewCount - 1);

  const activeSession = reviewSessions?.[selectedReview];
  const activeFeedback = activeSession?.revisedFeedback ?? revisedFeedback;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-100 text-blue-800">Revisado</Badge>
          {activeSession && (
            <span className="text-xs text-muted-foreground">
              por {activeSession.requestedByName} em{' '}
              {new Date(activeSession.completedAt || activeSession.requestedAt).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {reviewCount > 1 && (
            <div className="flex gap-1">
              {Array.from({ length: reviewCount }, (_, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={selectedReview === i ? 'default' : 'outline'}
                  className="h-6 w-6 p-0 text-xs"
                  onClick={() => setSelectedReview(i)}
                >
                  {i + 1}
                </Button>
              ))}
            </div>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowOriginal(!showOriginal)}
            className="text-xs"
          >
            {showOriginal ? 'Ver revisado' : 'Ver original'}
          </Button>
        </div>
      </div>

      {/* Score comparison */}
      <Card className="p-4 space-y-1">
        <h4 className="font-semibold text-sm mb-2">Comparação de Scores</h4>
        <ScoreDelta label="Técnico" original={originalFeedback.technicalScore} revised={activeFeedback.technicalScore} />
        <ScoreDelta label="Comportamental" original={originalFeedback.behavioralScore} revised={activeFeedback.behavioralScore} />
        <ScoreDelta label="Comunicação" original={originalFeedback.communicationScore} revised={activeFeedback.communicationScore} />
        <div className="border-t pt-1 mt-1">
          <ScoreDelta label="Score Geral" original={originalFeedback.overallScore} revised={activeFeedback.overallScore} />
        </div>
      </Card>

      {/* Justification */}
      {activeFeedback.justification && (
        <Card className="p-4">
          <h4 className="font-semibold text-sm mb-2">Justificativa da Revisão</h4>
          <p className="text-sm text-muted-foreground">{activeFeedback.justification}</p>
        </Card>
      )}

      {/* Original feedback toggle */}
      {showOriginal && (
        <Card className="p-4 space-y-2 border-dashed">
          <h4 className="font-semibold text-sm text-muted-foreground">Avaliação Original</h4>
          <div className="text-sm space-y-1">
            <p><span className="font-medium">Técnico:</span> {originalFeedback.feedback.technical}</p>
            <p><span className="font-medium">Comportamental:</span> {originalFeedback.feedback.behavioral}</p>
            <p><span className="font-medium">Comunicação:</span> {originalFeedback.feedback.communication}</p>
          </div>
          <p className="text-sm text-muted-foreground mt-2">{originalFeedback.summary}</p>
        </Card>
      )}
    </div>
  );
}
